import React, { useEffect, useMemo, useState } from "react";
import { showAlert } from "../../alertService";
import { db, storage } from "../../firebase";
import { BITACORA_ACTIONS, logBitacora } from "../../utils/bitacora";
import { normalizeDepositoEstado, resolucionDeposito } from "../../utils/depositoEstado";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import { Badge } from "../badge";
import DialogoConfirmacion from "../shared/DialogoConfirmacion";
import ResolverDepositoDialog from "../shared/ResolverDepositoDialog";
import { Heading } from "../heading";
import { useVenue } from "../VenueProvider";
import MetodoPagoField from "../shared/MetodoPagoField";
import DocumentUploader from "../shared/DocumentUploader";
import { ACCENT, BTN_PRIMARIO, BTN_SECUNDARIO, fmtDate, fmtMoney } from "./shared";
import { Timestamp, addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { deleteObject } from "firebase/storage";
import { ComprobanteFooterEmbedded } from "../shared/ComprobanteFooter";
import { ref as stRef } from "firebase/storage";
import {
  BanknotesIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from "@heroicons/react/20/solid";

// ---------- Depósitos tab ----------
// Estado codes after normalization (see normalizeDepositoEstado):
//  - "activo"   → "En custodia"  (venue is still holding the deposit)
//  - "devuelto" → "Devuelto"     (returned to the proveedor)
//  - "aplicado" → "Aplicado"     (venue kept it — damages, no-show, etc.)
// Legacy "perdido" docs are normalized to "aplicado" on read.
const DEPOSITO_STATUS = {
  activo: { label: "En custodia", color: "amber" },
  devuelto: { label: "Devuelto", color: "emerald" },
  aplicado: { label: "Aplicado", color: "red" },
};

const DepositosTab = ({ proveedor, venueId, events, navigate, userInfo, user, soloLectura = false }) => {
  const [depositos, setDepositos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Resolver (aplicar / devolver / repartir) y reactivar viven en diálogos
  // compartidos; aquí solo se guarda qué depósito está en juego.
  const [resolviendo, setResolviendo] = useState(null);
  const [savingResolver, setSavingResolver] = useState(false);
  const [reactivando, setReactivando] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    if (!venueId || !proveedor?.id) return;
    const q = query(
      collection(db, "venues", venueId, "proveedores", proveedor.id, "depositos"),
      orderBy("fecha", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDepositos(snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, estado: normalizeDepositoEstado(data.estado) };
        }));
        setLoading(false);
      },
      (err) => {
        console.error("Depositos snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId, proveedor?.id]);

  // Parciales: en estado "devuelto" la porción devuelta cuenta en devuelto,
  // el resto (monto - montoDevuelto) cuenta en aplicado.
  const { totalActivo, totalDevuelto, totalAplicado } = depositos.reduce(
    (t, d) => {
      const monto = Number(d.monto) || 0;
      if (d.estado === "activo") {
        t.totalActivo += monto;
      } else if (d.estado === "devuelto") {
        const devuelto = Number(d.montoDevuelto ?? d.monto) || 0;
        t.totalDevuelto += devuelto;
        t.totalAplicado += Math.max(0, monto - devuelto);
      } else if (d.estado === "aplicado") {
        t.totalAplicado += monto;
      }
      return t;
    },
    { totalActivo: 0, totalDevuelto: 0, totalAplicado: 0 }
  );

  const handleCreate = async (data) => {
    if (!venueId || !proveedor?.id) return;
    try {
      const ref = collection(
        db,
        "venues",
        venueId,
        "proveedores",
        proveedor.id,
        "depositos"
      );
      const payload = {
        monto: Number(data.monto) || 0,
        fecha: data.fecha || serverTimestamp(),
        descripcion: data.descripcion?.trim() || "",
        metodo: data.metodo || null,
        eventId: data.eventId || null,
        eventNombre: data.eventNombre || null,
        estado: "activo",
        montoDevuelto: null,
        fechaDevolucion: null,
        notasDevolucion: null,
        comprobantePago: data.comprobante
          ? {
              ...data.comprobante,
              uploadedAt: serverTimestamp(),
              uploadedBy: user?.uid || null,
            }
          : null,
        comprobanteDevolucion: null,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      };
      const docRef = await addDoc(ref, payload);
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_CREATE,
          entity: "proveedor_deposito",
          entityId: docRef.id,
          entityName: proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            monto: payload.monto,
            eventNombre: payload.eventNombre,
          },
        },
        userInfo,
        user
      );
      setShowAdd(false);
      showAlert("Depósito registrado", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const handleUpdate = async (id, patch, bitacoraDetails = {}) => {
    if (!venueId || !proveedor?.id) return;
    try {
      const ref = doc(
        db,
        "venues",
        venueId,
        "proveedores",
        proveedor.id,
        "depositos",
        id
      );
      await updateDoc(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        venueId,
        {
          action:
            bitacoraDetails.action ||
            BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_EDIT,
          entity: "proveedor_deposito",
          entityId: id,
          entityName: proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            ...bitacoraDetails,
          },
        },
        userInfo,
        user
      );
      return true;
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
      return false;
    }
  };

  // Desenlace del depósito desde el diálogo compartido. Misma forma de doc
  // que antes (estado + montoDevuelto + fechaDevolucion) para que el ledger,
  // Cuentas y las tarjetas sigan leyendo igual; el comprobante de devolución
  // va embebido en el doc, como el del pago.
  const handleResolver = async (res) => {
    const d = resolviendo;
    if (!d) return;
    setSavingResolver(true);
    try {
      const r = resolucionDeposito(d.monto, res.montoAplicado);
      const hayDevolucion = (r.montoDevuelto || 0) > 0;
      const patchData = {
        estado: r.estado,
        montoDevuelto: r.montoDevuelto,
        // La fecha del desenlace es la que el ledger usa para fechar el
        // ingreso o la devolución (FRA-343).
        fechaDevolucion: inputToTimestamp(res.fechaStr) || serverTimestamp(),
        metodoDevolucion: hayDevolucion ? res.metodo || null : null,
        notasDevolucion: res.notas?.trim() || null,
      };
      if (res.archivo && hayDevolucion) {
        patchData.comprobanteDevolucion = {
          storagePath: res.archivo.storagePath,
          filename: res.archivo.filename,
          fileSize: res.archivo.fileSize,
          mimeType: res.archivo.mimeType,
          uploadedAt: serverTimestamp(),
          uploadedBy: user?.uid || null,
        };
      }
      const ok = await handleUpdate(d.id, patchData, {
        action: hayDevolucion
          ? BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_DEVOLUCION
          : BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_COBRO,
        monto: hayDevolucion ? r.montoDevuelto : d.monto,
        montoAplicado: r.montoAplicado,
        montoDevuelto: r.montoDevuelto || 0,
      });
      // Si Firestore rechazó el cambio, el toast ya lo dijo: el diálogo se
      // queda abierto con lo capturado (y su comprobante pendiente).
      if (!ok) return;
      if (hayDevolucion && r.montoAplicado > 0) {
        // Parcial: la bitácora cuenta las dos caras.
        logBitacora(
          venueId,
          {
            action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_COBRO,
            entity: "proveedor_deposito",
            entityId: d.id,
            entityName: proveedor.nombre || "",
            details: { monto: r.montoAplicado, parcial: true },
          },
          userInfo,
          user
        );
      }
      setResolviendo(null);
    } finally {
      setSavingResolver(false);
    }
  };

  const handleReactivar = async () => {
    const d = reactivando;
    if (!d) return;
    await handleUpdate(
      d.id,
      {
        estado: "activo",
        montoDevuelto: null,
        fechaDevolucion: null,
        notasDevolucion: null,
      },
      {
        action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_REACTIVAR,
        monto: d.monto,
        estadoAnterior: d.estado,
      }
    );
    setReactivando(null);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    try {
      const blobs = [
        deleteCandidate.comprobantePago?.storagePath,
        deleteCandidate.comprobanteDevolucion?.storagePath,
        deleteCandidate.comprobante?.storagePath,
      ].filter(Boolean);
      await deleteDoc(
        doc(
          db,
          "venues",
          venueId,
          "proveedores",
          proveedor.id,
          "depositos",
          deleteCandidate.id
        )
      );
      for (const path of blobs) {
        try { await deleteObject(stRef(storage, path)); }
        catch (err) {
          if (err?.code !== "storage/object-not-found") {
            console.error("Error deleting proveedor deposito comprobante blob:", err);
          }
        }
      }
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_DELETE,
          entity: "proveedor_deposito",
          entityId: deleteCandidate.id,
          entityName: proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            monto: deleteCandidate.monto,
          },
        },
        userInfo,
        user
      );
      setDeleteCandidate(null);
      showAlert("Depósito eliminado", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  return (
    <div className="space-y-4">
      {/* Hero: en custodia */}
      <div
        className="rounded-xl p-5 text-white shadow-sm"
        style={{ backgroundColor: ACCENT }}
      >
        <div className="flex items-center gap-2 mb-1">
          <BanknotesIcon className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wide opacity-90">
            En custodia
          </span>
        </div>
        <div className="text-3xl font-bold">{fmtMoney(totalActivo)}</div>
        <div className="text-sm opacity-90 mt-1">
          Aún retenido por el venue
        </div>
      </div>
      {/* Devuelto + Aplicado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Devuelto
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">
            {fmtMoney(totalDevuelto)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Reembolsado al proveedor
          </div>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Aplicado
          </div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {fmtMoney(totalAplicado)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Retenido por el venue (daños, penalización, etc.)
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <Heading level={3}>Depósitos en garantía</Heading>
          {!showAdd && !soloLectura && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className={BTN_PRIMARIO}
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Registrar depósito
            </button>
          )}
        </div>

        {showAdd && (
          <DepositoForm
            mode="create"
            events={events}
            proveedorId={proveedor.id}
            venueId={venueId}
            onCancel={() => setShowAdd(false)}
            onSubmit={handleCreate}
          />
        )}

        {loading ? (
          <div className="py-8 text-center text-zinc-400">Cargando...</div>
        ) : depositos.length === 0 && !showAdd ? (
          <div className="py-8 text-center text-zinc-400 italic">
            Aún no hay depósitos registrados.
          </div>
        ) : (
          <div className="space-y-2 mt-3">
            {depositos.map((d) => {
              const status =
                DEPOSITO_STATUS[d.estado] || DEPOSITO_STATUS.activo;
              if (editingId === d.id) {
                return (
                  <DepositoForm
                    key={d.id}
                    mode="edit"
                    initial={d}
                    events={events}
                    proveedorId={proveedor.id}
                    onCancel={() => setEditingId(null)}
                    onSubmit={async (patch) => {
                      await handleUpdate(d.id, {
                        monto: Number(patch.monto) || 0,
                        fecha: patch.fecha,
                        descripcion: patch.descripcion?.trim() || "",
                        metodo: patch.metodo || null,
                        eventId: patch.eventId || null,
                        eventNombre: patch.eventNombre || null,
                      }, { monto: Number(patch.monto) || 0 });
                      setEditingId(null);
                    }}
                  />
                );
              }
              return (
                <div
                  key={d.id}
                  className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-3"
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg font-bold text-zinc-900">
                          {fmtMoney(d.monto)}
                        </span>
                        <Badge color={status.color}>{status.label}</Badge>
                        {d.estado === "devuelto" &&
                          Number(d.montoDevuelto) < Number(d.monto) && (
                            <Badge color="orange">
                              Parcial: {fmtMoney(d.montoDevuelto)}
                            </Badge>
                          )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {fmtDate(d.fecha)}
                        {d.metodo && <> · {d.metodo}</>}
                        {d.eventNombre && (
                          <>
                            {" · "}
                            <button
                              type="button"
                              onClick={() =>
                                d.eventId &&
                                navigate(`/dashboard/eventos/${d.eventId}`)
                              }
                              className="text-zinc-600 hover:text-[#1b4d2e] hover:underline"
                            >
                              {d.eventNombre}
                            </button>
                          </>
                        )}
                      </div>
                      {d.descripcion && (
                        <div className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap">
                          {d.descripcion}
                        </div>
                      )}
                      {d.estado === "devuelto" && (
                        <div className="text-xs text-emerald-700 mt-2">
                          Devuelto el {fmtDate(d.fechaDevolucion)}
                          {d.metodoDevolucion && <> · {d.metodoDevolucion}</>}
                          {d.notasDevolucion && (
                            <span className="text-zinc-500">
                              {" — "}
                              {d.notasDevolucion}
                            </span>
                          )}
                        </div>
                      )}
                      {/* El motivo por el que el venue se lo quedó se captura
                          al resolver; aquí es donde se lee después. */}
                      {d.estado === "aplicado" && (
                        <div className="text-xs text-red-700 mt-2">
                          Aplicado el {fmtDate(d.fechaDevolucion)}
                          {d.notasDevolucion && (
                            <span className="text-zinc-500">
                              {" — "}
                              {d.notasDevolucion}
                            </span>
                          )}
                        </div>
                      )}
                      <ComprobanteFooterEmbedded
                        soloLectura={soloLectura}
                        storagePathPrefix={`venues/${venueId}/proveedores/${proveedor.id}/comprobantes`}
                        estado={d.estado}
                        comprobantePago={d.comprobantePago || d.comprobante || null}
                        comprobanteDevolucion={d.comprobanteDevolucion || null}
                        onAttachPago={(c) =>
                          handleUpdate(
                            d.id,
                            {
                              comprobantePago: {
                                ...c,
                                uploadedAt: serverTimestamp(),
                                uploadedBy: user?.uid || null,
                              },
                              comprobante: null,
                            },
                            { action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_EDIT, comprobantePagoAttached: true }
                          )
                        }
                        onAttachDevolucion={(c) =>
                          handleUpdate(
                            d.id,
                            {
                              comprobanteDevolucion: {
                                ...c,
                                uploadedAt: serverTimestamp(),
                                uploadedBy: user?.uid || null,
                              },
                            },
                            { action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_EDIT, comprobanteDevolucionAttached: true }
                          )
                        }
                        onRemovePago={() =>
                          handleUpdate(
                            d.id,
                            { comprobantePago: null, comprobante: null },
                            { action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_EDIT, comprobantePagoRemoved: true }
                          )
                        }
                        onRemoveDevolucion={() =>
                          handleUpdate(
                            d.id,
                            { comprobanteDevolucion: null },
                            { action: BITACORA_ACTIONS.PROVEEDOR_DEPOSITO_EDIT, comprobanteDevolucionRemoved: true }
                          )
                        }
                      />
                    </div>
                    {/* Resolver, reactivar, editar y borrar: no en solo lectura (P-FRA-13) */}
                    {!soloLectura && (
                    <div className="flex gap-1">
                      {d.estado === "activo" ? (
                        <button
                          type="button"
                          onClick={() => setResolviendo(d)}
                          className="text-xs px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          title="Resolver: aplicar, devolver o repartir el depósito"
                        >
                          Resolver
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setReactivando(d)}
                          className="text-xs px-2 py-1 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200"
                          title="Reactivar (vuelve a 'En custodia')"
                        >
                          Reactivar
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditingId(d.id)}
                        className="text-zinc-400 hover:text-zinc-600 p-1.5"
                        title="Editar"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteCandidate(d)}
                        className="text-zinc-400 hover:text-red-500 p-1.5"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ResolverDepositoDialog
        open={!!resolviendo}
        onClose={() => setResolviendo(null)}
        deposito={resolviendo}
        fuente="proveedor"
        storagePathPrefix={venueId && proveedor?.id ? `venues/${venueId}/proveedores/${proveedor.id}/comprobantes` : null}
        saving={savingResolver}
        onSubmit={handleResolver}
      />

      <DialogoConfirmacion
        open={!!reactivando}
        onClose={() => setReactivando(null)}
        titulo="Reactivar depósito"
        subtitulo={`${fmtMoney(reactivando?.monto)} · hoy marcado como ${
          reactivando?.estado === "devuelto" ? "devuelto" : "aplicado"
        }`}
        icono={BanknotesIcon}
        confirmar={{ label: "Sí, reactivar", onClick: handleReactivar }}
      >
        <p>
          Vuelve a <strong>En custodia</strong>: se borra la devolución o la
          aplicación registrada y el depósito deja de contar como ingreso o
          como devuelto en Cuentas hasta que lo resuelvas de nuevo.
        </p>
      </DialogoConfirmacion>

      <DialogoConfirmacion
        open={!!deleteCandidate}
        onClose={() => setDeleteCandidate(null)}
        titulo="Eliminar depósito"
        subtitulo={fmtMoney(deleteCandidate?.monto)}
        icono={TrashIcon}
        confirmar={{ label: "Eliminar", tono: "rojo", onClick: handleDelete }}
      >
        <p>
          Se borra el registro y sus comprobantes. Esta acción no se puede
          deshacer.
        </p>
      </DialogoConfirmacion>
    </div>
  );
};

const dateToInput = (ts) => {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const inputToTimestamp = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return Timestamp.fromDate(new Date(y, m - 1, d, 12, 0, 0));
};

const DepositoForm = ({
  mode = "create",
  initial = null,
  events,
  proveedorId,
  venueId,
  onCancel,
  onSubmit,
}) => {
  const [monto, setMonto] = useState(
    initial?.monto != null ? String(initial.monto) : ""
  );
  const [fechaStr, setFechaStr] = useState(
    initial?.fecha?.toDate ? dateToInput(initial.fecha) : dateToInput(Timestamp.now())
  );
  const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
  const [metodo, setMetodo] = useState(initial?.metodo || "");
  const [eventId, setEventId] = useState(initial?.eventId || "");
  const [pendingComprobante, setPendingComprobante] = useState(null);
  const { metodosPagoConDefaults: metodosPago } = useVenue();

  const isCreate = mode === "create";
  const storagePrefix = isCreate && venueId && proveedorId
    ? `venues/${venueId}/proveedores/${proveedorId}/comprobantes`
    : null;

  const cleanupPendingBlob = async () => {
    if (pendingComprobante?.storagePath) {
      try { await deleteObject(stRef(storage, pendingComprobante.storagePath)); } catch (_) {}
    }
  };

  const handleAttachUploaded = async (payload) => {
    await cleanupPendingBlob();
    setPendingComprobante({
      storagePath: payload.storagePath,
      filename: payload.filename,
      fileSize: payload.fileSize,
      mimeType: payload.mimeType,
    });
  };

  const handleRemoveComprobante = async () => {
    await cleanupPendingBlob();
    setPendingComprobante(null);
  };

  const handleCancelForm = async () => {
    await cleanupPendingBlob();
    onCancel();
  };

  // Only events where this proveedor is assigned are pickable. If editing an
  // existing deposit whose event no longer has the proveedor (e.g. it was
  // unassigned later), we still surface that event so the user can clear or
  // keep the stale link consciously.
  const linkedEvents = useMemo(() => {
    const matches = (events || [])
      .filter((e) => e.eventType !== "bloqueo")
      .filter(
        (e) =>
          Array.isArray(e.proveedoresAsignados) &&
          e.proveedoresAsignados.some((a) => a.proveedorId === proveedorId)
      )
      .sort((a, b) => {
        const at = a.startTime?.toMillis?.() || 0;
        const bt = b.startTime?.toMillis?.() || 0;
        return bt - at;
      });
    if (initial?.eventId && !matches.some((e) => e.id === initial.eventId)) {
      const stale = (events || []).find((e) => e.id === initial.eventId);
      if (stale) return [stale, ...matches];
    }
    return matches;
  }, [events, proveedorId, initial?.eventId]);
  const selectedEvent = linkedEvents.find((e) => e.id === eventId);

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  const canSubmit = Number(monto) > 0 && fechaStr;

  return (
    <div className="rounded-lg border border-[#1b4d2e]/30 dark:border-[#1b4d2e]/60 bg-white dark:bg-zinc-800/50 p-4 mb-3">
      <div className="text-sm font-medium mb-3">
        {mode === "create" ? "Nuevo depósito" : "Editar depósito"}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Monto <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              pattern="-?[0-9.,]*"
              value={monto ? fmtMXNInput(monto) : ""}
              onChange={(e) => setMonto(normalizeMoneyInput(e.target.value))}
              placeholder="0"
              className={inputClass + " pl-7"}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Fecha <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={fechaStr}
            onChange={(e) => setFechaStr(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Vincular a evento
          </label>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            disabled={linkedEvents.length === 0}
            className={inputClass + " bg-white disabled:opacity-50"}
          >
            <option value="">— Ninguno —</option>
            {linkedEvents.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre || "(sin nombre)"} · {fmtDate(e.startTime)}
              </option>
            ))}
          </select>
          {linkedEvents.length === 0 && (
            <div className="text-xs text-zinc-400 mt-1">
              Asigna primero este proveedor a un evento para poder vincularlo.
            </div>
          )}
        </div>
      </div>
      <div className="mb-3 sm:w-1/3">
        <label className="block text-xs font-medium text-zinc-500 mb-1">Método</label>
        <MetodoPagoField
          value={metodo}
          onChange={setMetodo}
          metodosPago={metodosPago}
          className={inputClass}
        />
      </div>
      <textarea
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
        placeholder="Concepto o descripción del depósito..."
        rows={2}
        className={inputClass + " mb-3"}
      />
      {isCreate && storagePrefix && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Comprobante (opcional)
          </label>
          {pendingComprobante ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-zinc-800">{pendingComprobante.filename}</div>
                <div className="text-xs text-zinc-400">Adjunto al registrar</div>
              </div>
              <button
                type="button"
                onClick={handleRemoveComprobante}
                className="text-xs text-zinc-500 hover:text-red-600"
              >
                Quitar
              </button>
            </div>
          ) : (
            <DocumentUploader
              storagePathPrefix={storagePrefix}
              buttonLabel="Adjuntar comprobante"
              withDownloadUrl={false}
              onUploaded={handleAttachUploaded}
            />
          )}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleCancelForm}
          className={BTN_SECUNDARIO}
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              monto,
              fecha: inputToTimestamp(fechaStr),
              descripcion,
              metodo: metodo || null,
              eventId: eventId || null,
              eventNombre: selectedEvent?.nombre || null,
              comprobante: isCreate ? pendingComprobante : null,
            })
          }
          className={BTN_PRIMARIO}
          style={{ backgroundColor: ACCENT }}
        >
          {mode === "create" ? "Registrar" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
};

export default DepositosTab;
